import { useEffect } from 'react';

import { useMedia } from 'react-use';
import styled, { css } from 'styled-components';
import SuccessPageButton from 'views/PostEnrollment/SuccessPageButtons';

import pendingEnrollmentHeaderImage from 'assets/images/pending_page/pending_enrollment_header_image.png';

import { useVendorsIframe } from 'explat/useVendorsIframe';

import { useTimeToCompleteEnrollment } from 'hooks/useCaptureTimeOnPage';

import { PostEnrollmentChimeLogo as ChimeLogo } from 'styles/styleMixins';

import { screenSizes } from 'theme/screenSizes';

import { deviceCardImageSrc } from 'utils/cardImageHelper';
import {
  executeOathScript,
  loadGoogleAnalytics,
  loadGoogleTagManager,
} from 'utils/postEnrollmentHelper';
import { IS_EMBEDDED } from 'utils/queryParamHelpers';
import { trackEnrollmentPendingPageViewed } from 'utils/segment/pageEvents';

const educationalMaterialsLink = 'https://www.chime.com/online-banking/';

const PageWrapper = styled.div`
  display: grid;
  grid-template-rows: 1fr auto;
  background: ${props => props.theme.colors.white};
  min-height: ${IS_EMBEDDED ? '95vh' : '80vh'};
  position: fixed;
  bottom: 0;

  ${props => css`
    ${props.theme.media.tablet`
        min-height: 100vh;
        position: inherit;
        grid-template-rows: auto;
        grid-template-columns: repeat(2, 300px);
        justify-content: center;
        align-content: center;
        grid-gap: 48px;
      `};
  `};
`;

const ContentWrapper = styled.div`
  display: grid;
  grid-template-rows: auto;
  align-content: start;
  row-gap: 24px;
  padding: 0 24px 24px;

  ${props => css`
    ${props.theme.media.tablet`
        padding: 0;
        grid-row: 2 / 3;
        grid-column: 1 / 2;
      `};
  `};
`;

const Header = styled.h1`
  color: ${props => props.theme.colors['content-primary']};
  font-family: ${props => props.theme.fonts.headerFont};
  font-weight: 700;
  font-size: 28px;
  line-height: 36px;
  text-align: center;
  margin: 0;

  ${props => css`
    ${props.theme.media.tablet`
        text-align: left;
      `};
  `};
`;

const Description = styled.p`
  color: ${props => props.theme.colors['content-primary']};
  font-family: ${props => props.theme.fonts.primaryFont};
  font-size: 16px;
  line-height: 22px;
  text-align: center;
  margin: 0;

  ${props => css`
    ${props.theme.media.tablet`
        text-align: left;
      `};
  `};
`;

const CardImage = styled.img`
  grid-row: 2 / 3;
  width: 100%;
  ${props => css`
    ${props.theme.media.tablet`
        grid-column: 2 / 3;
      `};
  `};
`;

const LinkContainer = styled.div`
  line-height: 22px;
  text-align: center;
  grid-row: 12 / 12;
`;

const Link = styled.a`
  color: ${props => props.theme.colors.util.logoBrandColor};
  font-weight: 700;
  text-align: center;
  text-decoration: none;
`;

const PendingPage = () => {
  const isTablet = useMedia(`(max-width: ${screenSizes.tablet}px)`);

  useTimeToCompleteEnrollment();
  const {
    isLoaded: isVendorsExperimentLoaded,
    isVariant: isVendorsIframeVariant,
  } = useVendorsIframe();

  useEffect(() => {
    trackEnrollmentPendingPageViewed();
    executeOathScript();
    loadGoogleAnalytics();
  }, []);

  useEffect(() => {
    if (isVendorsExperimentLoaded) {
      if (!isVendorsIframeVariant) {
        loadGoogleTagManager();
      }
    }

    window?.scrollTo(0, 0);
  }, [isVendorsExperimentLoaded, isVendorsIframeVariant]);

  return (
    <PageWrapper>
      <ChimeLogo />
      <ContentWrapper>
        <Header>
          {IS_EMBEDDED && (
            <CardImage
              alt="Chime enrollment pending page header image"
              src={pendingEnrollmentHeaderImage}
            />
          )}
          {IS_EMBEDDED
            ? 'We’re reviewing your application'
            : 'Download the app while we review your app'}
        </Header>
        <Description>
          {IS_EMBEDDED
            ? 'We’ll send you a confirmation email within 48 hours. Stay tuned! '
            : ' We’re reviewing your application and we’ll send you a confirmation email within 48 hours.'}
        </Description>
        {!IS_EMBEDDED && <SuccessPageButton eventContext="pending_page" />}
        {IS_EMBEDDED && (
          <LinkContainer>
            <Link href={educationalMaterialsLink} target="_blank">
              Get to know Chime
            </Link>
          </LinkContainer>
        )}
      </ContentWrapper>
      {!IS_EMBEDDED && (
        <CardImage
          alt="Chime app on top of a Chime Card"
          src={deviceCardImageSrc(isTablet, !IS_EMBEDDED, false)}
        />
      )}
    </PageWrapper>
  );
};

export default PendingPage;
